import { useMemo, type ReactNode } from "react";
import { Box, alpha, useTheme } from "@mui/material";
import { formatDataBR, rotuloCompetencia, STATUS_GERADA_LABEL, type Espelho, type EspelhoColaborador } from "@/api/escala.service";
import {
  formatarHoras,
  minimoDoDia,
  montarGrade,
  type CelulaGrade,
  type DiaColuna,
  type GradeEspelho,
  type TotaisColaborador,
} from "@/components/escala/espelho";
import { BLOCOS, corDaJornada } from "@/components/escala/cargaHoraria";
import { COR_FERIADO, CORES_IMPRESSAO, FAIXA_DOMINGO_IMPRESSAO, FAIXA_FERIADO_IMPRESSAO, ROTULO_TIPO, TIPOS_LEGENDA } from "@/components/escala/tiposDia";
import { ROTULO_TIPO_DE_DIA, quadroDeEntradas, quadroDeHorarios, type EntradasDoTipo } from "@/components/escala/quadrosImpressao";
import { paginarImpressao, type PaginaDaImpressao } from "@/components/escala/paginasImpressao";
import { COR_MARCA_ALTERADO, cantoAlterado, trianguloAlterado } from "@/components/escala/espelhoEstilos";

// Medidas da página, em mm. Cada parte tem altura fixa: é com elas que paginarImpressao distribui
// o conteúdo, já que a página é montada aqui e não pelo navegador.
const PAGINA_LARGURA = 297;
const PAGINA_ALTURA = 210;
const MARGEM_VERTICAL = 8;
const MARGEM_LATERAL = 8;
const ALTURA_CARTAO = 13;
// Cada linha de amostras da legenda, com AMOSTRAS_POR_LINHA.
const ALTURA_LEGENDA = 4.6;
const AMOSTRAS_POR_LINHA = 2;
// A linha com a página, no alto das páginas depois da primeira.
const ALTURA_TOPO = 4;
const ESPACO = 2.5;
const ALTURA_COLUNAS = 9;
const ALTURA_LINHA = 5.4;
const ALTURA_TOTAL = 4.6;
const RESPIRO_QUADROS = 4;
const QUADRO_BORDA = 4;
const QUADRO_TITULO = 4.5;
const QUADRO_LINHA = 4.2;
const LARGURA_NOME = 52;
const LARGURA_TOTAL = 9;
const ALTURA_UTIL = PAGINA_ALTURA - 2 * MARGEM_VERTICAL;
// O que sobra abaixo do cabeçalho na primeira página, e abaixo da linha da página nas outras; o
// ESPACO é o que separa a grade de cima.
const ALTURA_PRIMEIRA = ALTURA_UTIL - ALTURA_CARTAO - ESPACO;
const ALTURA_DEMAIS = ALTURA_UTIL - ALTURA_TOPO - ESPACO;

// Papel branco, independente do tema claro ou escuro do app. Os cinzas são neutros, para o preto e
// branco não ter cor nenhuma.
const PAPEL = {
  texto: "#222222",
  secundario: "#6b6b6b",
  divisor: "#e3e3e3",
  sutil: "#f3f3f3",
  domingo: FAIXA_DOMINGO_IMPRESSAO,
  feriado: FAIXA_FERIADO_IMPRESSAO,
  verde: "#16a34a",
  vermelho: "#b52e2e",
};
// No preto e branco, o feriado fica no cinza do domingo e o que era cor sai em preto.
const CINZA_PB = "#e6e6e6";
// O triângulo do dia alterado à mão, o da tela, no tamanho do selo.
const TRIANGULO_ALTERADO = {
  ...trianguloAlterado(),
  borderTop: `6px solid ${COR_MARCA_ALTERADO}`,
  borderLeft: "6px solid transparent",
};

const mm = (valor: number) => `${valor}mm`;
// A linha que divide as linhas da grade: sombra em vez de borda, para não somar na altura da linha.
const divisa = (cor = PAPEL.divisor) => ({ boxShadow: `inset 0 -1px 0 ${cor}` });
const caixaAlta = {
  color: PAPEL.secundario,
  fontWeight: 700,
  fontSize: 6,
  textTransform: "uppercase",
  letterSpacing: 0.4,
} as const;
const umaLinha = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as const;

const TOTAIS = [
  { rotulo: "Trabalhando", chave: "trabalhando", checaMinimo: true },
  { rotulo: "Folgando", chave: "folgando", checaMinimo: false },
  { rotulo: "Compensação", chave: "compensando", checaMinimo: false },
] as const;

interface EspelhoImpressaoProps {
  espelho: Espelho;
  // "dd/mm/aaaa hh:mm — Nome", como no cabeçalho do espelho.
  validadaEm?: string;
  aprovadaEm?: string;
  // "dd/mm/aaaa hh:mm", no canto de cada página.
  impressoEm?: string;
  // Sem as cores dos tipos de dia e da carga.
  pretoBranco?: boolean;
}

// Contexto que as partes da página dividem.
interface Desenho {
  pretoBranco: boolean;
  primaria: string;
  primariaTexto: string;
  comCarga: boolean;
}

// A escala impressa no visual da grade da tela: o cabeçalho em cartão, o dia em selo colorido e as
// faixas de domingo e feriado. O cabeçalho vai só na primeira página; o resto vem um embaixo do
// outro, e a legenda fecha a impressão. Com carga horária, a célula mostra a entrada na cor da
// carga, "*" no dia com a carga trocada à mão e, antes da legenda, os quadros de horários e de entradas.
export function EspelhoImpressao({ espelho, validadaEm, aprovadaEm, impressoEm, pretoBranco = false }: EspelhoImpressaoProps) {
  const theme = useTheme();
  const { gerada, feriados, colaboradores } = espelho;
  const comCarga = !!gerada.com_carga_horaria;
  const grade = useMemo(() => montarGrade(espelho), [espelho]);
  const horarios = useMemo(() => (comCarga ? quadroDeHorarios(espelho) : null), [espelho, comCarga]);
  const entradas = useMemo(() => (comCarga ? quadroDeEntradas(espelho) : null), [espelho, comCarga]);
  // Sábado, domingo e feriado só entram nos quadros quando alguém trabalhou com horário neles: uma
  // escala sem domingo trabalhado, ou um mês sem feriado, não leva coluna vazia. O dia útil fica sempre.
  const blocos = horarios ? BLOCOS.filter((b) => b === "util" || horarios[b].length > 0) : [];
  const linhasHorarios = horarios ? Math.max(0, ...blocos.map((b) => horarios[b].length)) : 0;
  const linhasEntradas = entradas ? Math.max(0, ...blocos.map((b) => entradas[b].linhas.length)) : 0;

  const desenho: Desenho = {
    pretoBranco,
    primaria: theme.palette.primary.main,
    primariaTexto: theme.palette.primary.contrastText,
    comCarga,
  };
  // Os quadros ficam numa linha só, então a altura é a do mais alto. Em linhas de QUADRO_LINHA: nas
  // tabelas, os cabeçalhos, as linhas e o total; nos feriados, um por linha; na legenda, as linhas
  // de amostras.
  // Os tipos, "Alterado à mão" e as faixas (uma no preto e branco, domingo e feriado juntos).
  const linhasDeAmostras = Math.ceil((TIPOS_LEGENDA.length + 1 + (pretoBranco ? 1 : 2)) / AMOSTRAS_POR_LINHA);
  const alturaQuadros = Math.max(
    QUADRO_BORDA + QUADRO_TITULO + linhasDeAmostras * ALTURA_LEGENDA,
    ...(horarios ? [alturaDoQuadro(1 + Math.max(1, linhasHorarios)), alturaDoQuadro(3 + linhasEntradas)] : []),
    ...(feriados.length > 0 ? [alturaDoQuadro(feriados.length)] : []),
  );
  const paginas = paginarImpressao(colaboradores.length, {
    primeira: ALTURA_PRIMEIRA,
    demais: ALTURA_DEMAIS,
    colunas: ALTURA_COLUNAS,
    linha: ALTURA_LINHA,
    totais: (TOTAIS.length + grade.cargas.length) * ALTURA_TOTAL,
    quadros: alturaQuadros,
    respiro: RESPIRO_QUADROS,
  });
  const larguraDia = (PAGINA_LARGURA - 2 * MARGEM_LATERAL - LARGURA_NOME - colunasDeTotal(desenho) * LARGURA_TOTAL) / grade.colunas.length;

  return (
    <Box
      sx={{
        color: PAPEL.texto,
        bgcolor: "#fff",
        fontFamily: theme.typography.fontFamily,
        lineHeight: 1.2,
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
    >
      {paginas.map((pagina, i) => (
        <Box
          key={i}
          component="section"
          sx={{
            width: mm(PAGINA_LARGURA),
            // Um pouco menos que a folha, para o arredondamento não empurrar uma página em branco.
            minHeight: mm(PAGINA_ALTURA - 0.5),
            boxSizing: "border-box",
            p: `${mm(MARGEM_VERTICAL)} ${mm(MARGEM_LATERAL)}`,
            breakAfter: i < paginas.length - 1 ? "page" : "auto",
          }}
        >
          {i === 0 ? (
            <>
              <Cartao
                desenho={desenho}
                espelho={espelho}
                validadaEm={validadaEm}
                aprovadaEm={aprovadaEm}
                impressoEm={impressoEm}
                pagina={i + 1}
                paginas={paginas.length}
              />
            </>
          ) : (
            <Topo impressoEm={impressoEm} pagina={i + 1} paginas={paginas.length} />
          )}
          {pagina.grade ? (
            <Grade
              desenho={desenho}
              grade={grade}
              colaboradores={colaboradores}
              pagina={pagina}
              larguraDia={larguraDia}
              minimo={gerada.quantidade_minima}
              minimoDomingo={gerada.quantidade_minima_domingo}
            />
          ) : null}
          {pagina.quadros && (
            // Se um dia não couber na largura, quebra a linha em vez de cortar na borda da folha.
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: mm(3), mt: mm(RESPIRO_QUADROS) }}>
              {horarios && entradas && (
                <>
                  <Quadro titulo="Horários de trabalho">
                    <Box component="table" sx={tabelaQuadro}>
                      <thead>
                        <tr>
                          {blocos.map((b) => (
                            <th key={b}>{ROTULO_TIPO_DE_DIA[b]}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {linhasHorarios === 0 ? (
                          <tr>
                            <td colSpan={blocos.length}>Nenhum horário na escala</td>
                          </tr>
                        ) : (
                          Array.from({ length: linhasHorarios }, (_, l) => (
                            <tr key={l}>
                              {blocos.map((b) => {
                                const h = horarios[b][l];
                                return (
                                  <td key={b} style={{ color: corDaHora(desenho, h?.cor) }}>
                                    {h?.texto}
                                  </td>
                                );
                              })}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Box>
                  </Quadro>
                  <Quadro titulo="Colaboradores por horário de entrada">
                    <Box sx={{ display: "flex", gap: mm(1.5), alignItems: "flex-start" }}>
                      {blocos.map((b) => (
                        <TabelaEntradas key={b} rotulo={ROTULO_TIPO_DE_DIA[b]} entradas={entradas[b]} />
                      ))}
                    </Box>
                  </Quadro>
                </>
              )}
              {feriados.length > 0 && (
                <Quadro titulo="Feriados do mês">
                  {feriados.map((f) => (
                    <LinhaDoQuadro key={f.data}>
                      <Box component="span" sx={{ fontWeight: 700, mr: mm(1.5) }}>
                        {formatDataBR(f.data)}
                      </Box>
                      <span>
                        {f.nome}
                        {f.fecha_loja ? " (loja fechada)" : ""}
                      </span>
                    </LinhaDoQuadro>
                  ))}
                </Quadro>
              )}
              <Quadro titulo="Legenda">
                <Legenda desenho={desenho} />
              </Quadro>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

const alturaDoQuadro = (linhas: number) => QUADRO_BORDA + QUADRO_TITULO + linhas * QUADRO_LINHA;

// TT e TF, e o TH das horas do mês na escala com carga, como na tela.
const colunasDeTotal = (desenho: Desenho) => (desenho.comCarga ? 3 : 2);

function LinhaDoQuadro({ children }: { children: ReactNode }) {
  return <Box sx={{ fontSize: 7, height: mm(QUADRO_LINHA), display: "flex", alignItems: "center", ...umaLinha }}>{children}</Box>;
}

// No alto das páginas depois da primeira, no lugar do cabeçalho: só a hora da impressão e a página.
function Topo({ impressoEm, pagina, paginas }: { impressoEm?: string; pagina: number; paginas: number }) {
  return (
    <Box
      sx={{
        height: mm(ALTURA_TOPO),
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: mm(1.5),
        fontSize: 6.5,
        color: PAPEL.secundario,
      }}
    >
      {impressoEm && (
        <>
          <span>Impresso em {impressoEm}</span>
          <span>|</span>
        </>
      )}
      <Box component="span" sx={{ fontWeight: 700 }}>
        Página {pagina} de {paginas}
      </Box>
    </Box>
  );
}

// A cor da carga sai como foi escolhida, como na tela clara; sem ela ("Padrão"), quem chama decide.
const corDaHora = (desenho: Desenho, cor?: string) => (desenho.pretoBranco ? "#000" : corDaJornada(cor));

const faixaDoDia = (desenho: Desenho, c: Pick<DiaColuna, "domingo" | "feriado">) => {
  if (c.feriado) return desenho.pretoBranco ? CINZA_PB : PAPEL.feriado;
  if (c.domingo) return desenho.pretoBranco ? CINZA_PB : PAPEL.domingo;
  return undefined;
};

interface CartaoProps {
  desenho: Desenho;
  espelho: Espelho;
  validadaEm?: string;
  aprovadaEm?: string;
  impressoEm?: string;
  pagina: number;
  paginas: number;
}

// O cabeçalho do espelho, em cartão, com os mesmos campos da tela: rótulo e valor lado a lado, e o
// status em selo depois deles.
function Cartao({ desenho, espelho, validadaEm, aprovadaEm, impressoEm, pagina, paginas }: CartaoProps) {
  const { gerada } = espelho;
  return (
    <Box
      sx={{
        height: mm(ALTURA_CARTAO),
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        columnGap: mm(6),
        px: mm(4),
        border: `1px solid ${PAPEL.divisor}`,
        borderRadius: mm(2.5),
      }}
    >
      <Campo rotulo="Escala" largura={60}>
        <Box component="span" sx={{ fontSize: 10, fontWeight: 700 }}>
          {gerada.escala_nome}
        </Box>
      </Campo>
      <Campo rotulo="Loja" largura={42}>
        {gerada.loja_nome}
      </Campo>
      <Campo rotulo="Competência">{rotuloCompetencia(gerada.ano, gerada.mes)}</Campo>
      {validadaEm && (
        <Campo rotulo="Validada em" largura={40}>
          {validadaEm}
        </Campo>
      )}
      {aprovadaEm && (
        <Campo rotulo="Aprovada em" largura={40}>
          {aprovadaEm}
        </Campo>
      )}
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          px: mm(2),
          py: "1px",
          borderRadius: 99,
          fontSize: 6.5,
          fontWeight: 700,
          ...(desenho.pretoBranco ? { border: "1px solid #000", color: "#000" } : { bgcolor: PAPEL.verde, color: "#fff" }),
        }}
      >
        {STATUS_GERADA_LABEL[gerada.status]}
      </Box>
      <Box sx={{ ml: "auto", flexShrink: 0, textAlign: "right", fontSize: 6.5, color: PAPEL.secundario, lineHeight: 1.5 }}>
        {impressoEm && <Box>Impresso em {impressoEm}</Box>}
        <Box sx={{ fontWeight: 700 }}>
          Página {pagina} de {paginas}
        </Box>
      </Box>
    </Box>
  );
}

function Campo({ rotulo, largura, children }: { rotulo: string; largura?: number; children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0, flexShrink: largura ? 1 : 0, maxWidth: largura ? mm(largura) : undefined }}>
      <Box sx={{ fontSize: 6.5, color: PAPEL.secundario, mb: "1px" }}>{rotulo}</Box>
      <Box sx={{ fontSize: 8.5, fontWeight: 600, ...umaLinha }}>{children}</Box>
    </Box>
  );
}

// Selo do dia, como o da grade da tela: o dia alterado à mão (tipo ou carga) ganha o triângulo no
// canto, e o da carga trocada à mão, também o "*" depois da hora.
function Selo({
  desenho,
  tipo,
  texto,
  cor,
  manual,
  alterado,
  largura = 5.8,
}: {
  desenho: Desenho;
  tipo: string;
  texto: string;
  cor?: string;
  manual?: boolean;
  alterado?: boolean;
  largura?: number;
}) {
  const estilo = CORES_IMPRESSAO[tipo];
  const pb = desenho.pretoBranco;
  return (
    <Box
      component="span"
      sx={{
        position: "relative",
        display: "inline-grid",
        placeItems: "center",
        boxSizing: "border-box",
        width: mm(largura),
        height: mm(3.9),
        borderRadius: mm(0.9),
        fontSize: texto.length > 2 ? 6.4 : 7,
        fontWeight: tipo === "T" ? 600 : 800,
        bgcolor: pb ? undefined : estilo?.bg,
        border: pb ? "0.75px solid #9a9a9a" : undefined,
        verticalAlign: "middle",
        ...(alterado && cantoAlterado),
      }}
      style={{ color: pb ? "#000" : (cor ?? estilo?.fg) }}
    >
      <span>
        {texto}
        {manual && (
          // Mais baixo, para o triângulo do canto não cobrir.
          <Box component="span" sx={{ position: "relative", top: "2px", ml: "0.5px", fontWeight: 800, color: "#000" }}>
            *
          </Box>
        )}
      </span>
      {alterado && <Box component="span" aria-hidden sx={TRIANGULO_ALTERADO} />}
    </Box>
  );
}

function Legenda({ desenho }: { desenho: Desenho }) {
  const item = (chave: string, amostra: ReactNode, texto: string) => (
    <Box key={chave} sx={{ display: "flex", alignItems: "center", gap: mm(1.2) }}>
      {amostra}
      <span>{texto}</span>
    </Box>
  );
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${AMOSTRAS_POR_LINHA}, max-content)`,
        gridAutoRows: mm(ALTURA_LEGENDA),
        alignItems: "center",
        columnGap: mm(4),
        fontSize: 6.8,
        color: PAPEL.secundario,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {TIPOS_LEGENDA.map((tipo) =>
        tipo === "T" && desenho.comCarga
          ? item(
              tipo,
              <Selo desenho={desenho} tipo="T" texto="08:00" cor={CORES_IMPRESSAO.T!.fg} largura={7} />,
              "Trabalha (entrada)",
            )
          : item(tipo, <Selo desenho={desenho} tipo={tipo} texto={tipo} largura={4.8} />, ROTULO_TIPO[tipo]),
      )}
      {item("alterado", <Selo desenho={desenho} tipo="T" texto="T" largura={4.8} alterado />, "Alterado à mão")}
      {desenho.pretoBranco ? (
        item("domingo", <Faixa cor={CINZA_PB} />, "Domingo e feriado")
      ) : (
        <>
          {item("feriado", <Faixa cor={PAPEL.feriado} />, "Feriado")}
          {item("domingo", <Faixa cor={PAPEL.domingo} />, "Domingo")}
        </>
      )}
    </Box>
  );
}

function Faixa({ cor }: { cor?: string }) {
  return <Box component="span" sx={{ width: mm(4.8), height: mm(3.9), borderRadius: mm(0.9), bgcolor: cor }} />;
}

interface GradeProps {
  desenho: Desenho;
  grade: GradeEspelho;
  colaboradores: EspelhoColaborador[];
  pagina: PaginaDaImpressao;
  larguraDia: number;
  minimo: number;
  minimoDomingo?: number;
}

function Grade({ desenho, grade, colaboradores, pagina, larguraDia, minimo, minimoDomingo }: GradeProps) {
  const { colunas } = grade;
  return (
    <Box
      component="table"
      sx={{
        mt: mm(ESPACO),
        width: "100%",
        tableLayout: "fixed",
        borderCollapse: "collapse",
        "& th, & td": { p: 0, textAlign: "center" },
      }}
    >
      <colgroup>
        <col style={{ width: mm(LARGURA_NOME) }} />
        {colunas.map((c) => (
          <col key={c.dia} style={{ width: mm(larguraDia) }} />
        ))}
        {Array.from({ length: colunasDeTotal(desenho) }, (_, i) => (
          <col key={`total-${i}`} style={{ width: mm(LARGURA_TOTAL) }} />
        ))}
      </colgroup>
      <thead>
        <Box component="tr" sx={{ height: mm(ALTURA_COLUNAS), "& th": divisa() }}>
          <Box component="th" sx={{ ...caixaAlta, textAlign: "left !important", pl: mm(3) }}>
            Colaborador
          </Box>
          {colunas.map((c) => {
            const especial = c.domingo || !!c.feriado;
            const destaque = desenho.pretoBranco ? "#333" : c.feriado ? COR_FERIADO : desenho.primaria;
            return (
              <Box component="th" key={c.dia} sx={{ bgcolor: faixaDoDia(desenho, c), ...divisa() }}>
                <Box sx={{ fontSize: 5.8, fontWeight: 600, color: especial ? destaque : PAPEL.secundario }}>{c.letra}</Box>
                <Box
                  sx={{
                    mx: "auto",
                    mt: "1px",
                    width: mm(4.2),
                    height: mm(4.2),
                    lineHeight: mm(4.2),
                    borderRadius: "50%",
                    fontSize: 7,
                    fontWeight: 700,
                    bgcolor: especial ? destaque : undefined,
                    color: especial ? (desenho.pretoBranco || c.feriado ? "#fff" : desenho.primariaTexto) : PAPEL.texto,
                  }}
                >
                  {c.dia}
                </Box>
              </Box>
            );
          })}
          <Box component="th" sx={{ ...caixaAlta, boxShadow: `inset 1px 0 0 ${PAPEL.divisor}, inset 0 -1px 0 ${PAPEL.divisor}` }}>
            TT
          </Box>
          <Box component="th" sx={caixaAlta}>
            TF
          </Box>
          {desenho.comCarga && (
            <Box component="th" sx={caixaAlta}>
              TH
            </Box>
          )}
        </Box>
      </thead>
      <tbody>
        {colaboradores.slice(pagina.inicio, pagina.fim).map((colaborador) => (
          <LinhaColaborador
            key={colaborador.id}
            desenho={desenho}
            colaborador={colaborador}
            colunas={colunas}
            celulas={grade.celulas.get(colaborador.id)}
            totais={grade.porColaborador.get(colaborador.id)!}
          />
        ))}
      </tbody>
      {pagina.totais && <Totais desenho={desenho} grade={grade} minimo={minimo} minimoDomingo={minimoDomingo} />}
    </Box>
  );
}

function iniciais(nome: string) {
  const partes = nome.trim().split(/\s+/);
  return ((partes[0]?.[0] ?? "") + (partes.length > 1 ? partes.at(-1)![0]! : "")).toUpperCase();
}

function subtitulo(colaborador: EspelhoColaborador) {
  const partes = [colaborador.matricula ? `Mat. ${colaborador.matricula}` : "Sem matrícula"];
  if (colaborador.turma) partes.push(`Turma ${colaborador.turma}`);
  return partes.join(" · ");
}

function LinhaColaborador({
  desenho,
  colaborador,
  colunas,
  celulas,
  totais,
}: {
  desenho: Desenho;
  colaborador: EspelhoColaborador;
  colunas: DiaColuna[];
  celulas?: Map<number, CelulaGrade>;
  totais: TotaisColaborador;
}) {
  return (
    <Box component="tr" sx={{ height: mm(ALTURA_LINHA), "& td": divisa() }}>
      <Box component="td" sx={{ textAlign: "left !important", pl: mm(3), pr: mm(1.5) }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: mm(1.8), minWidth: 0 }}>
          <Box
            aria-hidden
            sx={{
              flexShrink: 0,
              width: mm(4),
              height: mm(4),
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              fontSize: 5.5,
              fontWeight: 700,
              bgcolor: desenho.pretoBranco ? "#ececec" : alpha(desenho.primaria, 0.12),
              color: desenho.pretoBranco ? "#000" : desenho.primaria,
            }}
          >
            {iniciais(colaborador.nome)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 7.4, fontWeight: 600, ...umaLinha }}>{colaborador.nome}</Box>
            <Box sx={{ fontSize: 5.8, color: PAPEL.secundario, ...umaLinha }}>{subtitulo(colaborador)}</Box>
          </Box>
        </Box>
      </Box>
      {colunas.map((c) => {
        const celula = celulas?.get(c.dia);
        return (
          <Box component="td" key={c.dia} sx={{ bgcolor: faixaDoDia(desenho, c) }}>
            {celula?.tipo && (
              <Selo
                desenho={desenho}
                tipo={celula.tipo}
                texto={celula.entrada ?? celula.rotulo}
                alterado={celula.alterado}
                cor={celula.tipo === "T" && desenho.comCarga ? corDaHora(desenho, celula.cor) : undefined}
                manual={celula.manual}
              />
            )}
          </Box>
        );
      })}
      <Box component="td" sx={{ fontSize: 7.5, fontWeight: 700, boxShadow: `inset 1px 0 0 ${PAPEL.divisor}, inset 0 -1px 0 ${PAPEL.divisor}` }}>
        {totais.trabalhados}
      </Box>
      <Box component="td" sx={{ fontSize: 7.5, fontWeight: 700, color: desenho.pretoBranco ? "#000" : PAPEL.vermelho }}>
        {totais.folgas}
      </Box>
      {desenho.comCarga && (
        <Box component="td" sx={{ fontSize: 6.8, fontWeight: 700, whiteSpace: "nowrap" }}>
          {formatarHoras(totais.minutos)}
        </Box>
      )}
    </Box>
  );
}

// As linhas de totais do rodapé da tela: quem trabalha, folga e compensa no dia, e uma por carga.
function Totais({
  desenho,
  grade,
  minimo,
  minimoDomingo,
}: {
  desenho: Desenho;
  grade: GradeEspelho;
  minimo: number;
  minimoDomingo?: number;
}) {
  const linhas = [
    ...TOTAIS.map((t) => ({ chave: t.chave, rotulo: t.rotulo, valores: grade.porDia.map((d) => d[t.chave]), checaMinimo: t.checaMinimo })),
    ...grade.cargas.map((c) => ({
      chave: `carga-${c.id}`,
      rotulo: c.descricao,
      valores: c.porDia,
      checaMinimo: false,
      cor: desenho.pretoBranco ? undefined : corDaJornada(c.cor),
    })),
  ];
  return (
    <tfoot>
      {linhas.map((linha, i) => {
        const cor = "cor" in linha ? linha.cor : undefined;
        return (
          <Box
            component="tr"
            key={linha.chave}
            sx={{
              height: mm(ALTURA_TOTAL),
              bgcolor: PAPEL.sutil,
              "& td": i === 0 ? { boxShadow: `inset 0 1.5px 0 ${PAPEL.divisor}` } : {},
            }}
          >
            <Box
              component="td"
              sx={{
                textAlign: "left !important",
                pl: mm(3),
                ...caixaAlta,
                ...("cor" in linha && { textTransform: "none", letterSpacing: 0, fontSize: 6.8 }),
                ...umaLinha,
              }}
              style={cor ? { color: cor } : undefined}
            >
              {linha.rotulo}
            </Box>
            {linha.valores.map((valor, idx) => {
              const coluna = grade.colunas[idx]!;
              const abaixo = linha.checaMinimo && valor < minimoDoDia(coluna, minimo, minimoDomingo) && !coluna.fechaLoja;
              return (
                <Box component="td" key={coluna.dia} sx={{ fontSize: 6.8, fontWeight: 600 }}>
                  {abaixo ? (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        minWidth: mm(4),
                        borderRadius: mm(0.8),
                        fontWeight: 800,
                        ...(desenho.pretoBranco
                          ? { border: "0.75px solid #000", color: "#000" }
                          : { bgcolor: alpha(PAPEL.vermelho, 0.14), color: PAPEL.vermelho }),
                      }}
                    >
                      {valor}
                    </Box>
                  ) : (
                    <Box component="span" sx={{ color: PAPEL.secundario }} style={cor ? { color: cor } : undefined}>
                      {valor}
                    </Box>
                  )}
                </Box>
              );
            })}
            <Box component="td" colSpan={colunasDeTotal(desenho)} />
          </Box>
        );
      })}
    </tfoot>
  );
}

const tabelaQuadro = {
  borderCollapse: "collapse",
  fontSize: 6.5,
  "& tr": { height: mm(QUADRO_LINHA) },
  "& th, & td": { px: mm(1.3), textAlign: "center", whiteSpace: "nowrap", boxShadow: `inset 0 -1px 0 ${PAPEL.divisor}` },
  "& th": { bgcolor: PAPEL.sutil, ...caixaAlta, fontSize: 5.5 },
} as const;

function Quadro({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <Box sx={{ border: `1px solid ${PAPEL.divisor}`, borderRadius: mm(2.5), px: mm(2.5), py: mm(QUADRO_BORDA / 2 - 0.3) }}>
      <Box sx={{ ...caixaAlta, height: mm(QUADRO_TITULO) }}>{titulo}</Box>
      {children}
    </Box>
  );
}

function TabelaEntradas({ rotulo, entradas }: { rotulo: string; entradas: EntradasDoTipo }) {
  return (
    <Box component="table" sx={tabelaQuadro}>
      <thead>
        <tr>
          <th colSpan={2}>{rotulo}</th>
        </tr>
        <tr>
          <th>Horário</th>
          <th>Qtde colab.</th>
        </tr>
      </thead>
      <tbody>
        {entradas.linhas.map((l) => (
          <tr key={l.hora}>
            <td>{l.hora}H</td>
            <td>{l.qtde}</td>
          </tr>
        ))}
        <Box component="tr" sx={{ fontWeight: 700 }}>
          <td>Total</td>
          <td>{entradas.total}</td>
        </Box>
      </tbody>
    </Box>
  );
}
